import { autoinject, BindingEngine, observable, transient, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { HistoryHelper } from "../../../../../../../../helpers/history-helper";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../../../../../models/queries/queries.models";
import { TasksConstants } from "../../../../../../../../models/tasks/task.model";
import { SignalFunctions } from "../../../../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { Guid } from "../../../../../../../../utilities/guid";
import { CustomGridPageBase } from "../../../../../../common/custom-grid-page.base";
import { FormsActivities } from "../../../../../../admin/activities/forms-activity/forms-activity";

@autoinject
@transient()
@useView('views/pages/modules/auto-screening/templates/pre-arrival-risk-review/tabs/forms/pre-arrival-risk-review-forms.html')
export class PreArrivalRiskReviewForm extends CustomGridPageBase {

    public defaultPageCount: number = 50;
    public accessibleFormIds: number[] = [];

    public getItemRoute = (id) => `/vet-task-onboarding-custom-content?vesselApplicationFormId=${id}`;
    public initialFilters: QueryFilter[]; 

    protected get odataUrl() {
        return "odata/VetTaskActivities";
    }

    @observable()
    filter: string;
    appliedFilter: string;

    filterKeyDown($event) {
        const e: any = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.reloadItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.reloadItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.reloadItemSource();
    }

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private signalClientService: SignalClientService) {
        super(i18N, TasksConstants.FormsListName, bindingEngine);
    }

    vesselId:number;
    activate(params: { vesselId: number }) {
        this.vesselId = params.vesselId;
        this.columns = [];
        this.columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.Number,
                name: 'workflowStatusId',
                isHidden: true,
                isFilterable: true
            },
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'formName',
                title: 'Form',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "200px",
                value:
                    '<div class="form" if.bind="item.formName">' +
                    '<span class="name"><a class="link" href.bind="getItemRoute(item)" mousedown.trigger="itemClick()">${item.formName}</a></span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                    getItemRoute: this.getRoute.bind(this),
                    itemClick: this.itemClick.bind(this)
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 7,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'submittedDate',
                title: 'Submitted Date',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "150px",
                valueConverter: (date: Date | string) => date ? moment(date).format('DD MMM YYYY HH:mm') : '',
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.DateTime,
                isSortable: false,
                name: 'validityExpiryDate',
                title: 'Valid Until',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "110px",
                valueConverter: (date: Date | string) => date ? moment(date).format('DD MMM YYYY') : '- -',
            },
            <DataGridColumn>{
                columnOrder: 7,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                isFilterable: true,
                name: 'workflowStatusNotes',
                title: 'Status',
                value:
                    '<div class="status-badge-value">' +
                    '   <div class="row nowrap align-items-middle">' +
                    '       <div class="status-badge-dot"></div>' +
                    '       <span>${getStatus(item)}</span>' +
                    '   </div>' +
                    '   <span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                getCellClass: (item: { workflowStatusNotes: string }) => {
                    const status = item.workflowStatusNotes;
                    return status ? `status-badge ${status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: { // the reason we have this is to override the status with status that we want for Forms
                    item: null,
                    getStatus: (item: any) => {
                        return item.workflowStatusNotes;
                    },
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });

        this.initialSorting = this.getSorting();
        this.reloadItemSource();
    }

    public itemClick() {
        HistoryHelper.addState(TasksConstants.historyTaskTabKey, this.itemsName);
        return true;
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.formStatusChanged, this.refreshFormStatus);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.formStatusChanged, this.refreshFormStatus);
    }

    refreshFormStatus = (param) => {
        if (this.dataGridVM) {
            if (this.dataGridVM.page == 0) {
                this.dataGridVM.reloadData();
            }
            else {
                const item = this.dataGridVM.itemsCollection.filter(x => x.item.vesselApplicationFormId == param.entityId)[0];

                if (item) {
                    item.item.status = FormsActivities.formsStatuses.filter(x => x.name == param.statusNotes).map(x => x.name)[0];
                }
            }
        }
    }

    getRoute(item) {
        return this.getItemRoute(item.vesselApplicationFormId);
    }

    reloadItemSource() {
        const initialFilters = [];

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("formName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
        }

        if (this.vesselId) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("vesselId", this.vesselId.toString(), "{0} eq {1}", DataType.Default, true, false, guid, true, true));
        }

        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: initialFilters,
            initialSorting: this.getSorting()
        };
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];

        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: false,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            });
        return columns;
    }
}
